// src/pages/league-dashboard/LeagueStandings.js - COMPLETE WITH TEAM LINK DROPDOWNS
import React, { useState, useEffect } from 'react';
import { Trophy, Calendar, TrendingUp } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import LeagueBanner from '../../components/league-dashboard/LeagueBanner';
import StandingsDateStepper from '../../components/mobile/components/StandingsDateStepper';
import RotisserieTable from './standings/RotisserieTable';
import HeadToHeadStandings from './standings/HeadToHeadStandings';

const LeagueStandings = ({ league, teams, leagueId, userTeam, leagueBanner, bannerConfig, hideBanner = false, isMobile = false }) => {
  const [standingsData, setStandingsData] = useState(null);
  const [currentWeekData, setCurrentWeekData] = useState(null);
  const [teamsWithLogos, setTeamsWithLogos] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [throughDate, setThroughDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [scoringCategories, setScoringCategories] = useState({
    hitting: ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'],
    pitching: ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS']
  });
  
  // Fetch league settings for dynamic categories
  useEffect(() => {
    fetchLeagueSettings();
  }, [leagueId]);
  
  // Fetch team data including logos
  useEffect(() => {
    fetchTeamsWithLogos();
  }, [teams, leagueId]);
  
  useEffect(() => {
    if ((league?.league_settings?.scoring_system === 'rotisserie' || 
         league?.league_settings?.scoring_system === 'roto' || 
         league?.league_settings?.scoring_system === 'rotisserie_ytd') && 
        teamsWithLogos.length > 0) {
      fetchAllStandings();
    }
  }, [league?.league_settings?.scoring_system, teamsWithLogos, scoringCategories]);

  const fetchLeagueSettings = async () => {
    try {
      const response = await leaguesAPI.getLeagueSettings(leagueId);
      if (response.success && response.settings) {
        if (response.settings.scoring_categories) {
          const categories = response.settings.scoring_categories;
          
          setScoringCategories({
            hitting: categories.hitting || scoringCategories.hitting,
            pitching: categories.pitching || scoringCategories.pitching
          });
        } else if (response.league?.rotisserie_hitting_categories) {
          // Fallback to league object if present
          setScoringCategories({
            hitting: response.league.rotisserie_hitting_categories || response.league.hitting_categories || scoringCategories.hitting,
            pitching: response.league.rotisserie_pitching_categories || response.league.pitching_categories || scoringCategories.pitching
          });
        }
      }
    } catch (error) {
      console.log('Could not fetch league settings, using defaults:', error);
    }
  };

  const fetchTeamsWithLogos = async () => {
    if (!leagueId) {
      setTeamsWithLogos([]);
      return;
    }
    
    try {
      setLoadingData(true);
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      if (response.success && response.teams) {
        setTeamsWithLogos(response.teams);
      } else {
        // Fallback to fetching individual team data
        const teamsWithDetails = await Promise.all(
          teams.map(async (team) => {
            try {
              const teamResponse = await leaguesAPI.getTeamRosterCanonical(leagueId, team.team_id);
              return {
                ...team,
                team_logo_url: teamResponse.team?.team_logo_url || team.team_logo_url,
                team_colors: teamResponse.team?.team_colors || team.team_colors
              };
            } catch (e) {
              return team;
            }
          })
        );
        setTeamsWithLogos(teamsWithDetails);
      }
    } catch (error) {
      console.log('Could not fetch team details:', error);
      setTeamsWithLogos(teams);
    } finally {
      setLoadingData(false);
    }
  };

  const formatStandingsResponse = (response) => ({
    categories: response.categories ?? scoringCategories,
    teams: (response.standings ?? []).map(team => ({
      ...team,
      stats: team.stats,
      totalPoints: team.total_points ?? team.totalPoints ?? 0,
      category_points: team.category_points,
      category_ranks: team.category_ranks,
    }))
  });

  const fetchAllStandings = async () => {
    try {
      const [seasonRes, weekRes, datesRes] = await Promise.all([
        leaguesAPI.getLeagueStandings(leagueId),
        leaguesAPI.getLeagueStandings(leagueId, 'week'),
        leaguesAPI.getStandingsAvailableDates(leagueId),
      ]);

      if (seasonRes.success && seasonRes.standings) {
        setStandingsData(formatStandingsResponse(seasonRes));
        if (seasonRes.through_date) {
          setThroughDate(seasonRes.through_date);
          setSelectedDate(seasonRes.through_date);
        }
      }

      if (weekRes.success && weekRes.standings) {
        setCurrentWeekData(formatStandingsResponse(weekRes));
      }

      if (datesRes.success && datesRes.dates) {
        setAvailableDates(datesRes.dates);
      }
    } catch (error) {
      console.error('Error fetching standings:', error);
    }
  };

  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
    try {
      // Refetch BOTH views as-of the selected date: season standings through the
      // date, AND the scoring-week standings week-to-date as of that day. The week
      // view was previously never refetched on date nav, so "This Week's" stayed
      // frozen at its mount-time (current-week) value.
      const [seasonRes, weekRes] = await Promise.all([
        leaguesAPI.getLeagueStandings(leagueId, 'season', newDate),
        leaguesAPI.getLeagueStandings(leagueId, 'week', newDate),
      ]);
      if (seasonRes.success && seasonRes.standings) {
        setStandingsData(formatStandingsResponse(seasonRes));
        setThroughDate(seasonRes.through_date);
      }
      if (weekRes.success && weekRes.standings) {
        setCurrentWeekData(formatStandingsResponse(weekRes));
      }
    } catch (err) {
      console.error('Error fetching historical standings:', err);
    }
  };

  // DD-STAT-CONSOLIDATION C6: removed dead createStandingsData — a Math.random()
  // standings MOCK that fabricated category values. Never invoked. Real standings
  // come from the API; totals/ranks from the backend + SSoT engine.

  return (
  <div className="space-y-6">
  {/* Banner Header */}
  {!hideBanner && (
  <LeagueBanner
  leagueBanner={leagueBanner}
  title={(league?.league_settings?.scoring_system === 'rotisserie' || league?.league_settings?.scoring_system === 'roto' || league?.league_settings?.scoring_system === 'rotisserie_ytd') ? 'Rotisserie Standings' : 'League Standings'}
  subtitle={`${league?.league_name} • ${league?.season} Season • ${teams.length}/${league?.league_settings?.max_teams || 12} Teams`}
  minHeight="160px"
  bannerConfig={bannerConfig}
  />
  )}

  {/* Scoring Categories Info */}
  {scoringCategories && (
  <div className={`${dynastyTheme.components.card.base} p-4`}>
  <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
    <span className={dynastyTheme.classes.text.primary}>Categories:</span> {[...scoringCategories.hitting, ...scoringCategories.pitching].join(', ')}
  </p>
  </div>
  )}

      {(league?.league_settings?.scoring_system === 'rotisserie' || 
        league?.league_settings?.scoring_system === 'roto' || 
        league?.league_settings?.scoring_system === 'rotisserie_ytd') ? (
        <>
          {/* Season-Long Standings */}
          <RotisserieTable
            data={standingsData}
            title="Season Standings - Overall"
            throughDate={throughDate}
            icon={Trophy}
            userTeam={userTeam}
            isMobile={isMobile}
            league={league}
            leagueId={leagueId}
            dateStepper={
              availableDates.length > 0 && selectedDate && (
                <StandingsDateStepper
                  currentDate={selectedDate}
                  availableDates={availableDates}
                  onDateChange={handleDateChange}
                />
              )
            }
          />

          {/* Current Week Standings */}
          <RotisserieTable
            data={currentWeekData}
            title="Current Week Standings"
            icon={Calendar}
            userTeam={userTeam}
            isMobile={isMobile}
            league={league}
            leagueId={leagueId}
          />
        </>
      ) : (
        <HeadToHeadStandings
          teamsWithLogos={teamsWithLogos}
          league={league}
          leagueId={leagueId}
        />
      )}
    </div>
  );
};

export default LeagueStandings;